import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Chip,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Checkbox
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import "./UserLessonsTable.css";

const UserLessonsTable = ({ 
  userLessons, 
  loading, 
  hasSearched,
  totalRecords,
  isAllDataLoaded,
  onDeleteUserLesson, 
  onUpdateProgress,
  selectedIds = [],
  onSelectionChange = () => {} 
}) => {
  const [orderBy, setOrderBy] = useState('lastName');
  const [order, setOrder] = useState('asc');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUserLessonId, setSelectedUserLessonId] = useState(null);
  const [currentProgress, setCurrentProgress] = useState(null);

  // Function to format dates to local timezone with DD/MM/YYYY HH:MM:SS AM/PM format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return 'N/A';
      
      // Format to DD/MM/YYYY HH:MM:SS AM/PM in user's local timezone
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      // Convert to 12-hour format with AM/PM
      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      
      // Convert to 12-hour format
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      const formattedHours = hours.toString().padStart(2, '0');
      
      return `${day}/${month}/${year} ${formattedHours}:${minutes}:${seconds} ${ampm}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleMenuOpen = (event, userLessonId, progress) => {
    setAnchorEl(event.currentTarget);
    setSelectedUserLessonId(userLessonId);
    setCurrentProgress(progress);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserLessonId(null);
    setCurrentProgress(null);
  };

  const handleProgressUpdate = (newProgress) => {
    if (onUpdateProgress && selectedUserLessonId) {
      onUpdateProgress(selectedUserLessonId, newProgress);
    }
    handleMenuClose();
  };

  const getAvailableStatusOptions = (currentProgress) => {
    const options = [];
    
    if (currentProgress !== 0) {
      options.push({
        label: "No Iniciado",
        progress: 0,
        icon: <PlayArrowIcon sx={{ fontSize: '16px', color: '#ff9800', mr: 1 }} />,
        color: '#ff9800',
        backgroundColor: 'rgba(255, 152, 0, 0.1)',
        border: '1px solid rgba(255, 152, 0, 0.2)'
      });
    }
    
    if (currentProgress !== 40) {
      options.push({
        label: "En Progreso",
        progress: 40,
        icon: <AutorenewIcon sx={{ fontSize: '16px', color: '#2196f3', mr: 1 }} />,
        color: '#2196f3',
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        border: '1px solid rgba(33, 150, 243, 0.2)'
      });
    }
    
    if (currentProgress !== 100) {
      options.push({
        label: "Completado",
        progress: 100,
        icon: <CheckCircleIcon sx={{ fontSize: '16px', color: '#4caf50', mr: 1 }} />,
        color: '#4caf50',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        border: '1px solid rgba(76, 175, 80, 0.2)'
      });
    }
    
    return options;
  };

  const getStatusBadge = (progress, userLessonId) => {
    // Determine which chip to show based on progress
    let chipProps = {};
    
    if (progress === 0) {
      chipProps = {
        label: "No Iniciado",
        icon: <PlayArrowIcon sx={{ fontSize: '16px !important', color: '#ff9800 !important' }} />,
        backgroundColor: 'rgba(255, 152, 0, 0.1)',
        color: '#ff9800',
        border: '1px solid rgba(255, 152, 0, 0.2)'
      };
    } else if (progress >= 1 && progress <= 99) {
      chipProps = {
        label: "En Progreso",
        icon: <AutorenewIcon sx={{ fontSize: '16px !important', color: '#2196f3 !important' }} />,
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        color: '#2196f3',
        border: '1px solid rgba(33, 150, 243, 0.2)'
      };
    } else if (progress === 100) {
      chipProps = {
        label: "Completado",
        icon: <CheckCircleIcon sx={{ fontSize: '16px !important', color: '#4caf50 !important' }} />,
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        color: '#4caf50',
        border: '1px solid rgba(76, 175, 80, 0.2)'
      };
    }

    return (
      <Chip
        icon={chipProps.icon}
        label={chipProps.label}
        size="small"
        onClick={(event) => handleMenuOpen(event, userLessonId, progress)}
        sx={{
          backgroundColor: chipProps.backgroundColor,
          color: chipProps.color,
          fontWeight: 500,
          border: chipProps.border,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            opacity: 0.8,
            transform: 'scale(1.02)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
          '& .MuiChip-icon': {
            marginLeft: '4px'
          }
        }}
      />
    );
  };

  const sortedUserLessons = [...userLessons].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    // Handle numeric values for progress
    if (orderBy === 'progress') {
      aValue = parseInt(aValue || 0);
      bValue = parseInt(bValue || 0);
    }
    
    // Handle date values
    if (orderBy === 'startedAt' || orderBy === 'updatedAt' || orderBy === 'createdAt') {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    }

    // Handle string comparison
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  // Handle checkbox selection (moved after sortedUserLessons definition)
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = sortedUserLessons.map(ul => ul.id);
      onSelectionChange(allIds);
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectOne = (userLessonId) => {
    const currentIndex = selectedIds.indexOf(userLessonId);
    const newSelected = [...selectedIds];

    if (currentIndex === -1) {
      newSelected.push(userLessonId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    onSelectionChange(newSelected);
  };

  const isSelected = (id) => selectedIds.indexOf(id) !== -1;
  const isIndeterminate = selectedIds.length > 0 && selectedIds.length < sortedUserLessons.length;
  const isAllSelected = sortedUserLessons.length > 0 && selectedIds.length === sortedUserLessons.length;

  if (loading) {
    return (
      <Box className="user-lessons-loading">
        <CircularProgress size={40} />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Cargando lecciones de usuarios...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} sx={{ 
        marginTop: 2, 
        boxShadow: 3,
        overflowX: 'auto',
        minWidth: '100%'
      }}>
        <Table sx={{ minWidth: 1000 }}>
        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={isIndeterminate}
                checked={isAllSelected}
                onChange={handleSelectAll}
                inputProps={{ 'aria-label': 'seleccionar todas las filas' }}
              />
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'firstName'}
                direction={orderBy === 'firstName' ? order : 'asc'}
                onClick={() => handleSort('firstName')}
              >
                Nombre
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'lastName'}
                direction={orderBy === 'lastName' ? order : 'asc'}
                onClick={() => handleSort('lastName')}
              >
                Apellido
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'email'}
                direction={orderBy === 'email' ? order : 'asc'}
                onClick={() => handleSort('email')}
              >
                Email
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'lessonTitle'}
                direction={orderBy === 'lessonTitle' ? order : 'asc'}
                onClick={() => handleSort('lessonTitle')}
              >
                Lección
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'progress'}
                direction={orderBy === 'progress' ? order : 'asc'}
                onClick={() => handleSort('progress')}
              >
                Estado
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', minWidth: '180px' }}>
              <TableSortLabel
                active={orderBy === 'startedAt'}
                direction={orderBy === 'startedAt' ? order : 'asc'}
                onClick={() => handleSort('startedAt')}
              >
                Fecha de Inicio
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', minWidth: '200px' }}>
              <TableSortLabel
                active={orderBy === 'updatedAt'}
                direction={orderBy === 'updatedAt' ? order : 'asc'}
                onClick={() => handleSort('updatedAt')}
              >
                Última Actualización
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedUserLessons.map((userLesson) => (
            <TableRow 
              key={userLesson.id} 
              hover
              onClick={() => handleSelectOne(userLesson.id)}
              sx={{ 
                '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                cursor: 'pointer',
                ...(isSelected(userLesson.id) && {
                  backgroundColor: '#e3f2fd !important',
                  '&:hover': {
                    backgroundColor: '#bbdefb !important',
                  }
                })
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isSelected(userLesson.id)}
                  onChange={() => handleSelectOne(userLesson.id)}
                  inputProps={{ 'aria-labelledby': `enhanced-table-checkbox-${userLesson.id}` }}
                />
              </TableCell>
              <TableCell>{userLesson.firstName || 'N/A'}</TableCell>
              <TableCell>{userLesson.lastName || 'N/A'}</TableCell>
              <TableCell>{userLesson.email || 'N/A'}</TableCell>
              <TableCell>
                <Typography variant="body2">
                  {userLesson.lessonTitle || 'Lección sin título'}
                </Typography>
              </TableCell>
              <TableCell>
                {getStatusBadge(userLesson.progress || 0, userLesson.id)}
              </TableCell>
              <TableCell sx={{ minWidth: '180px' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  {formatDate(userLesson.startedAt)}
                </Typography>
              </TableCell>
              <TableCell sx={{ minWidth: '200px' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  {formatDate(userLesson.updatedAt)}
                </Typography>
              </TableCell>
              <TableCell>
                <IconButton
                  color="secondary"
                  onClick={() => onDeleteUserLesson(userLesson.id)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {sortedUserLessons.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} align="center">
                <Typography variant="body2" color="text.secondary">
                  {!hasSearched 
                    ? "Usa los filtros de búsqueda para encontrar lecciones de usuarios"
                    : "No se encontraron lecciones de usuarios con los criterios especificados"
                  }
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      
      {hasSearched && (
        <Box sx={{ 
          p: 2, 
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#fafafa'
        }}>
          <Typography variant="body2" color="text.secondary">
            Mostrando {userLessons.length} registro{userLessons.length !== 1 ? 's' : ''} (todos los registros cargados)
          </Typography>
        </Box>
      )}
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {getAvailableStatusOptions(currentProgress).map((option) => (
          <MenuItem
            key={option.progress}
            onClick={() => handleProgressUpdate(option.progress)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: '160px',
              py: 1,
              px: 2,
              '&:hover': {
                backgroundColor: option.backgroundColor,
                '& .MuiTypography-root': {
                  fontWeight: 600
                }
              }
            }}
          >
            {option.icon}
            <Typography variant="body2" sx={{ color: option.color, fontWeight: 500 }}>
              {option.label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </TableContainer>
    </Box>
  );
};

export default UserLessonsTable;
